import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { WorkingHour } from "@/model/entities";
import { useWorkingHours } from "@/query/useWorkingHours";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const initialHours: WorkingHour[] = [
  { day: "Sunday", open: "", close: "", isVacation: false },
  { day: "Monday", open: "", close: "", isVacation: false },
  { day: "Tuesday", open: "", close: "", isVacation: false },
  { day: "Wednesday", open: "", close: "", isVacation: false },
  { day: "Thursday", open: "", close: "", isVacation: false },
  { day: "Friday", open: "", close: "", isVacation: false },
  { day: "Saturday", open: "", close: "", isVacation: false },
];

const WorkingHoursPage: React.FC = () => {
  const { t } = useTranslation();
  const [workingHours, setWorkingHours] = useState<WorkingHour[]>(initialHours);
  const [defaultOpen, setDefaultOpen] = useState("");
  const [defaultClose, setDefaultClose] = useState("");

  const { restaurantId } = useCurrentRestaurant();
  const { data: firebaseWorkingHours, addWorkingHours } = useWorkingHours(
    restaurantId!
  );

  useEffect(() => {
    if (firebaseWorkingHours && firebaseWorkingHours.length > 0) {
      setWorkingHours(firebaseWorkingHours);
    }
  }, [firebaseWorkingHours]);

  const applyDefaultHours = () => {
    const updated = workingHours.map((day) =>
      day.isVacation ? day : { ...day, open: defaultOpen, close: defaultClose }
    );
    setWorkingHours(updated);
  };

  const handleTimeChange = (
    index: number,
    field: "open" | "close",
    value: string
  ) => {
    const newHours = [...workingHours];
    newHours[index][field] = value;
    setWorkingHours(newHours);
  };

  const toggleVacation = (index: number) => {
    const updated = [...workingHours];
    updated[index].isVacation = !updated[index].isVacation;
    if (updated[index].isVacation) {
      updated[index].open = "";
      updated[index].close = "";
    }
    setWorkingHours(updated);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    for (const dayHours of workingHours) {
      if (
        !dayHours.isVacation &&
        dayHours.open &&
        dayHours.close &&
        dayHours.open >= dayHours.close
      ) {
        alert(
          t("workingHours.invalidTime", { day: t(`days.${dayHours.day}`) })
        );
        return;
      }
    }

    try {
      await addWorkingHours.mutateAsync(workingHours);
      toast.success(t("workingHours.saved"));
    } catch (error) {
      console.error("Error saving working hours:", error);
      toast.error(t("workingHours.errorSaving"));
    }
  };

  const dayOrder = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const sortedWorkingHours = workingHours.sort(
    (a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day)
  );

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1
        className="text-2xl font-semibold mb-6"
        data-testid="working-hours-title"
      >
        {t("workingHours.title")}
      </h1>

      <div className="mb-6 p-4 border border-gray-200 rounded-md">
        <h2 className="text-lg font-medium mb-2">
          {t("workingHours.defaultHours")}
        </h2>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="w-full md:w-auto">
            <label className="block text-sm">{t("workingHours.open")}</label>
            <input
              type="time"
              value={defaultOpen}
              onChange={(e) => setDefaultOpen(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              step="60"
              lang="en-GB"
            />
          </div>
          <div className="w-full md:w-auto">
            <label className="block text-sm">{t("workingHours.close")}</label>
            <input
              type="time"
              value={defaultClose}
              onChange={(e) => setDefaultClose(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              step="60"
              lang="en-GB"
            />
          </div>
          <button
            type="button"
            onClick={applyDefaultHours}
            className="h-10 px-4 bg-primary text-white rounded-md hover:bg-primary-dark w-full md:w-auto"
          >
            {t("workingHours.applyToAll")}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {sortedWorkingHours.map((dayHours, index) => (
          <React.Fragment key={dayHours.day}>
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 border-b border-gray-300 pb-4">
              <div className="w-full md:w-32 font-medium text-center md:text-left">
                {t(`days.${dayHours.day}`)}
              </div>
              {dayHours.isVacation ? (
                <div className="text-red-500 font-semibold">
                  {t("workingHours.vacation")}
                </div>
              ) : (
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <div className="w-full md:w-auto">
                    <label className="block text-sm">
                      {t("workingHours.open")}
                    </label>
                    <input
                      type="time"
                      value={dayHours.open}
                      onChange={(e) =>
                        handleTimeChange(index, "open", e.target.value)
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      step="60"
                      lang="en-GB"
                    />
                  </div>
                  <div className="w-full md:w-auto">
                    <label className="block text-sm">
                      {t("workingHours.close")}
                    </label>
                    <input
                      type="time"
                      value={dayHours.close}
                      onChange={(e) =>
                        handleTimeChange(index, "close", e.target.value)
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      step="60"
                      lang="en-GB"
                    />
                  </div>
                </div>
              )}
              <div className="w-full md:w-auto">
                <label className="block text-sm invisible">Toggle</label>
                <button
                  type="button"
                  onClick={() => toggleVacation(index)}
                  className={`mt-1 block px-3 py-2 rounded-md text-sm font-medium border w-full md:w-auto ${
                    dayHours.isVacation
                      ? "bg-red-100 text-red-600 border-red-300"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                  }`}
                >
                  {dayHours.isVacation
                    ? t("workingHours.cancelVacation")
                    : t("workingHours.markVacation")}
                </button>
              </div>
            </div>
          </React.Fragment>
        ))}
        <button
          type="submit"
          className="px-6 py-2 bg-primary text-white rounded-md shadow hover:bg-primary-dark w-full md:w-auto"
        >
          {t("workingHours.save")}
        </button>
      </form>
    </div>
  );
};

export default WorkingHoursPage;
